import React, {CSSProperties, KeyboardEvent, useCallback, useEffect, useRef} from "react"
import classNames from "classnames"
import Styles from "./robot.less"
import {CSSTransition} from "react-transition-group"
import mike from "./image/mike.png"
import "./index.less"
import Answer, {AnswerData} from "../Answer/answer"
import Input, {InputData} from "../Input/input"
import useCurrentRobotInput from "../Input/share/useCurrentRobotInput"
import RadioIcon, {RadioRefObj} from "../RadioIcon/radioIcon"
import useRadioActive from "../RadioIcon/share/useRadioActive"
import useRadioError from "../RadioIcon/share/useRadioError"
import {useDebounceFn} from "ahooks"
import {CardClickAction} from "../Card/card"
import {Loading} from "../Loading/loading"
import {Header} from "./Header/header"
import useScrollerStatus from "./share/useScrollerStatus"
import InputField from "../InputField/input"
import {EvaluationData} from "../Evaluation/evaluation"

export interface RobotProps {
    dialogList: Array<DialogItem>
    adaptWindow: boolean | null | undefined
    onInput: (text: any) => void
    onCardClick: (param: CardClickAction) => void
    onResetSession: () => void
    onWeltClick: () => void
    enableDraggingStatus?: () => void
    disableDraggingStatus?: () => void
    onEvaluated: (data: EvaluationData) => void
    onExit: () => void
    isFullScreen?: boolean
    isActive?: boolean
    isLoading?: boolean
    className?: string
    style?: CSSProperties
}

export type DialogItem = InputData | AnswerData

export const Robot: React.FC<RobotProps> = (props) => {
    const {
        dialogList,
        onInput,
        onCardClick,
        onResetSession,
        isActive,
        isLoading,
        className,
        onExit,
        style,
        onWeltClick,
        enableDraggingStatus = () => {},
        disableDraggingStatus = () => {},
    } = props
    const listEf = useRef<any>()
    const [value, setValue] = useCurrentRobotInput()
    const handleInputKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter") {
            handleSendMsg()
        }
    }

    const handleSendMsg = () => {
        if (value.trim().length === 0) {
            setValue("")
            return
        }
        onInput({eventData: value, show: true})
        setValue("")
    }

    const handleInputEdit = () => {
        focusInput()
    }

    const domList = dialogList.map((item, index) => {
        switch (item.type) {
            case "input":
                return (
                    <Input
                        key={index}
                        onMouseLeave={enableDraggingStatus}
                        onMouseEnter={disableDraggingStatus}
                        isFullScreen={props.isFullScreen}
                        onEdit={handleInputEdit}
                        data={item as InputData}
                    />
                )
            case "answer":
                return (
                    <Answer
                        key={index}
                        onMouseLeave={enableDraggingStatus}
                        onMouseEnter={disableDraggingStatus}
                        isFullScreen={props.isFullScreen}
                        onEvaluated={(data) => {
                            props.onEvaluated({
                                praise: data.praise,
                                id: (item as AnswerData).content?.id,
                            })
                        }}
                        onCardClick={onCardClick}
                        data={item as AnswerData}
                    />
                )
            default:
                return <div>unknown</div>
        }
    })

    useEffect(() => {
        let MAX_LENGTH = 1000000
        if (listEf.current.scrollTo !== undefined) {
            listEf.current?.scrollTo({
                top: MAX_LENGTH,
                behavior: "smooth",
            })
        }
    }, [domList.length, dialogList.length, isLoading])

    useEffect(() => {
        focusInput()
    }, [isActive])

    const classes = classNames(className, Styles["robot"])

    const sendBtnStyle = {
        color: value.length > 0 ? "#5582F3" : "#BBBBBB",
        cursor: value.length > 0 ? "pointer" : "auto",
    }

    const handleMikeClick = () => {
        radioRef.current?.onClick()
    }

    const radioRef = useRef<RadioRefObj>()

    const radioActive = useRadioActive()
    const radioError = useRadioError()

    const getRobotFooterHeight = () => {
        if (!(radioActive.value || radioError.value)) {
            return 65
        }
        if (radioActive.value || radioError.value) {
            // return 168
            return 65
        }
    }

    const robotFooterStyle = {
        // height: getRobotFooterHeight(),
        transition: "height 200ms",
    }

    const [robotInput, setRobotInput] = useCurrentRobotInput()
    const handleRadioInputChange = (text: string) => {
        setRobotInput(text)

        //  结束讲话后，麦克风收录到杂音，服务器一直返回同样的数据
        if (robotInput !== text) {
            stopRecodingAndSendInput()
        }
    }

    const inputRef = useRef<HTMLInputElement>(null)
    const {run: stopRecodingAndSendInput} = useDebounceFn(
        () => {
            radioRef.current?.onStopRecording()
            handleSendMsg()
            focusInput()
        },
        {
            wait: 1500,
        }
    )

    const focusInput = () => {
        inputRef.current?.focus()
    }

    const handleResetSession = () => {
        focusInput()
        onResetSession()
    }

    const showLoading = () => {
        if (!isLoading) {
            return null
        }
        return (
            <section>
                <Loading />
            </section>
        )
    }

    const handleListWheel = () => {
        scrollerStatus.setTrue()
    }

    const scrollerStatus = useScrollerStatus()
    const listClasses = classNames(Styles["list"], {
        [Styles["scroller"]]: !scrollerStatus.value,
    })
    return (
        <div className={classes} style={style}>
            {props.adaptWindow ? null : (
                <Header
                    isFullScreen={props.isFullScreen}
                    onResetSession={handleResetSession}
                    onWeltClick={onWeltClick}
                    onExit={onExit}
                />
            )}
            <div ref={listEf} onWheel={handleListWheel} className={listClasses}>
                {domList}
                {showLoading()}
            </div>
            <footer
                className={props.adaptWindow ? Styles["adaptFooter"] : Styles["footer"]}
                style={robotFooterStyle}
                onMouseLeave={enableDraggingStatus}
                onMouseEnter={disableDraggingStatus}
            >
                <div className={props.adaptWindow ? Styles["adaptFooter-content"] : Styles["footer-content"]}>
                    <div className={Styles.voiceIcon}>
                        <img src={mike} onClick={handleMikeClick} className={Styles["mike"]} />
                    </div>
                    <InputField
                        isActive={isActive}
                        onInputChange={(e) => setValue(e.target.value.trim())}
                        value={value}
                        onKeyDown={handleInputKeyDown}
                        onCompositionChange={() => {}}
                    />
                    <div style={sendBtnStyle} className={Styles.sendBtn} onClick={handleSendMsg}>
                        <span> 发送</span>
                    </div>
                </div>
                <CSSTransition
                    in={radioActive.value || radioError.value}
                    timeout={200}
                    classNames={"radio-opacity-change"}
                >
                    <RadioIcon ref={radioRef} onTextChange={useCallback(handleRadioInputChange, [])} />
                </CSSTransition>
            </footer>
        </div>
    )
}

export default Robot
